import React, { useEffect, useState } from "react";
import { Loader2, X, Download, AlertCircle } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { fetchFileBlob, fileUrl } from "@/lib/api";

const isImageExt = (e) => ["jpg", "jpeg", "png", "gif", "webp", "heic", "heif"].includes(e);
const isPdfExt = (e) => e === "pdf";

export const FilePreview = ({ open, onClose, path, title }) => {
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const ext = (path || "").split(".").pop().toLowerCase();
    const previewable = isPdfExt(ext) || isImageExt(ext);

    useEffect(() => {
        if (!open || !path || !previewable) return;
        let url;
        setLoading(true);
        setError(null);
        fetchFileBlob(path)
            .then((blob) => {
                url = URL.createObjectURL(blob);
                setBlobUrl(url);
            })
            .catch((e) => {
                setError(e?.response?.data?.detail || "No se pudo cargar el archivo");
            })
            .finally(() => setLoading(false));
        return () => {
            if (url) URL.revokeObjectURL(url);
            setBlobUrl(null);
        };
        // eslint-disable-next-line
    }, [open, path]);

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent
                className="rounded-xl max-w-5xl w-[95vw] h-[88vh] p-0 overflow-hidden flex flex-col"
                data-testid="file-preview-modal"
            >
                <DialogTitle className="sr-only">{title || "Vista previa"}</DialogTitle>
                <DialogDescription className="sr-only">Vista previa del archivo</DialogDescription>
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-white shrink-0">
                    <div className="min-w-0">
                        <div className="overline">Vista previa</div>
                        <div className="font-display text-base font-medium truncate">
                            {title || "Archivo"}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {path && (
                            <a href={fileUrl(path)} target="_blank" rel="noreferrer" download>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="rounded-xl border-slate-300"
                                    data-testid="preview-download"
                                >
                                    <Download size={13} className="mr-1" />
                                    Descargar
                                </Button>
                            </a>
                        )}
                        <button
                            onClick={onClose}
                            className="text-slate-500 hover:text-slate-900 p-1.5"
                            aria-label="Cerrar"
                            data-testid="preview-close"
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>
                <div className="flex-1 min-h-0 bg-slate-50 flex items-center justify-center overflow-auto">
                    {!previewable ? (
                        <div className="text-center px-8 py-10">
                            <AlertCircle className="text-slate-400 mx-auto mb-3" size={28} />
                            <div className="font-display text-lg font-medium mb-1">
                                Vista previa no disponible
                            </div>
                            <div className="text-sm text-slate-500 mb-5">
                                Este formato (.{ext}) no admite previsualización inline. Descarga
                                el archivo para verlo.
                            </div>
                            <a href={fileUrl(path)} target="_blank" rel="noreferrer" download>
                                <Button className="rounded-xl bg-[#0055ff] hover:bg-[#0044cc] text-white">
                                    <Download size={14} className="mr-1.5" />
                                    Descargar archivo
                                </Button>
                            </a>
                        </div>
                    ) : loading ? (
                        <div className="flex items-center gap-2 text-slate-500 text-sm">
                            <Loader2 className="animate-spin" size={16} />
                            Cargando…
                        </div>
                    ) : error ? (
                        <div className="text-center px-8">
                            <AlertCircle className="text-red-500 mx-auto mb-2" size={24} />
                            <div className="text-sm text-red-700">{error}</div>
                        </div>
                    ) : blobUrl ? (
                        isPdfExt(ext) ? (
                            <iframe
                                src={blobUrl}
                                title={title || "PDF"}
                                className="w-full h-full bg-white"
                                data-testid="preview-pdf"
                            />
                        ) : (
                            <img
                                src={blobUrl}
                                alt={title || ""}
                                className="max-w-full max-h-full object-contain"
                                data-testid="preview-image"
                            />
                        )
                    ) : null}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default FilePreview;
